import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { Slider } from "./components/ui/slider";
import { Checkbox } from "./components/ui/checkbox";
import { Progress } from "./components/ui/progress";
import { Loader2, Youtube, Sparkles, Target, Video, Share2, Download, Volume2, FileVideo } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [youtubeUrl, setYoutubeUrl] = useState("https://www.youtube.com/watch?v=dQw4w9WgXcQ"); // Default to working video
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [fartIntensity, setFartIntensity] = useState([5]);
  const [fartFrequency, setFartFrequency] = useState([5]);
  const [copyrightAcknowledged, setCopyrightAcknowledged] = useState(false);
  const [videoGenerationStatus, setVideoGenerationStatus] = useState(null);
  const [pollingAnalysisId, setPollingAnalysisId] = useState(null);
  const [activeTab, setActiveTab] = useState("youtube"); // "youtube" or "upload"

  const getIntensityLabel = (intensity) => {
    if (intensity <= 2) return "Subtle Whispers";
    if (intensity <= 4) return "Gentle Puffs";
    if (intensity <= 6) return "Classic Farts";
    if (intensity <= 8) return "Thunderous Blasts";
    return "Fully Loaded Cannons";
  };

  const getFrequencyLabel = (frequency) => {
    if (frequency <= 2) return "Subtle";
    if (frequency <= 4) return "Occasional";
    if (frequency <= 6) return "Regular";
    if (frequency <= 8) return "Frequent";
    return "Absolute Mayhem";
  };

  // Check if video is currently being processed
  const isProcessing = loading || pollingAnalysisId !== null || videoGenerationStatus === "pending" || videoGenerationStatus === "processing";

  const analyzeVideo = async () => {
    if (activeTab === "upload" && selectedFile) {
      return analyzeUploadedVideo();
    } else if (activeTab === "youtube" && youtubeUrl.trim()) {
      return analyzeVideoUrl();
    }
  };

  const analyzeVideoUrl = async () => {
    if (!youtubeUrl.trim()) {
      toast.error("Please enter a video URL");
      return;
    }

    if (!copyrightAcknowledged) {
      toast.error("Please acknowledge the entertainment disclaimer");
      return;
    }

    setLoading(true);
    setVideoGenerationStatus(null);
    
    try {
      const response = await axios.post(`${API}/analyze`, {
        youtube_url: youtubeUrl,
        generate_video: true, // Always generate video in simplified UI
        fart_intensity: fartIntensity[0],
        fart_frequency: fartFrequency[0],
        copyright_acknowledged: copyrightAcknowledged
      });
      
      setAnalysis(response.data);
      
      toast.success("Adding gas to your vid... 💨");
      const analysisId = response.data.analysis_id;
      if (analysisId) {
        setPollingAnalysisId(analysisId);
        setVideoGenerationStatus("processing");
      }
      
      // No need to loadHistory in simplified UI
    } catch (error) {
      console.error("Analysis failed:", error);
      const errorMessage = error.response?.data?.detail || "Analysis failed. Try again!";
      
      if (errorMessage.includes("blocked") || errorMessage.includes("IP")) {
        toast.error("Video is temporarily unavailable. Try the guaranteed working videos below! 🔄");
      } else if (errorMessage.includes("403") || errorMessage.includes("Forbidden")) {
        toast.error("This video is blocked or forbidden. Use one of the green 'guaranteed to work' buttons! 🚫");
        // Auto-fill with working video
        setYoutubeUrl("https://www.youtube.com/watch?v=dQw4w9WgXcQ");
      } else if (errorMessage.includes("captions") || errorMessage.includes("subtitles")) {
        toast.error("This video needs captions. Try the Rick Roll or Gangnam Style buttons! 📝");
        setYoutubeUrl("https://www.youtube.com/watch?v=dQw4w9WgXcQ");
      } else if (errorMessage.includes("unsupported")) {
        toast.error("This video URL is not supported. Try YouTube, Twitter/X, or other supported platforms!");
      } else {
        toast.error(errorMessage + " Try one of the guaranteed working videos!");
      }
    } finally {
      setLoading(false);
    }
  };

  const analyzeUploadedVideo = async () => {
    if (!selectedFile) {
      toast.error("Please select a video file to upload");
      return;
    }

    if (!copyrightAcknowledged) {
      toast.error("Please acknowledge the entertainment disclaimer");
      return;
    }

    setLoading(true);
    setVideoGenerationStatus(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('generate_video', true); // Always generate video in simplified UI
      formData.append('fart_intensity', fartIntensity[0]);
      formData.append('fart_frequency', fartFrequency[0]);
      formData.append('copyright_acknowledged', copyrightAcknowledged);

      const response = await axios.post(`${API}/upload-analyze`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setAnalysis(response.data);
      
      toast.success("Adding gas to your vid... 💨");
      const analysisId = response.data.analysis_id;
      if (analysisId) {
        setPollingAnalysisId(analysisId);
        setVideoGenerationStatus("processing");
      }
      
      // No need to loadHistory in simplified UI
    } catch (error) {
      console.error("Upload analysis failed:", error);
      const errorMessage = error.response?.data?.detail || "Upload failed. Try again!";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Poll for video generation status
  useEffect(() => {
    let pollInterval;
    
    if (pollingAnalysisId) {
      console.log("Starting polling for analysis ID:", pollingAnalysisId);
      pollInterval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/analysis/${pollingAnalysisId}`);
          const status = response.data.video_generation_status;
          console.log("Polling status:", status, response.data);
          
          if (status === "completed") {
            setVideoGenerationStatus("completed");
            const videoURL = response.data.generated_video_url;
            console.log("Completed! Video URL:", videoURL);
            console.log("Full response data:", response.data);
            
            // Update analysis with video URL using spread operator for reliability
            setAnalysis(prevAnalysis => {
              if (!prevAnalysis) {
                console.error("No analysis to update!");
                return prevAnalysis;
              }
              
              console.log("Current analysis before update:", prevAnalysis);
              
              // Use spread operator to create new object and ensure re-render
              const updatedAnalysis = {
                ...prevAnalysis,
                generated_video_url: videoURL,
                video_generation_status: "completed"
              };
              
              console.log("Updated analysis object:", updatedAnalysis);
              console.log("Video URL in updated analysis:", updatedAnalysis.generated_video_url);
              
              return updatedAnalysis;
            });
            
            toast.success("Video generation complete! 🎉");
            setPollingAnalysisId(null);
          } else if (status === "failed") {
            setVideoGenerationStatus("failed");
            toast.error("Video generation failed. Try again later.");
            setPollingAnalysisId(null);
          } else if (status === "processing") {
            setVideoGenerationStatus("processing");
          }
        } catch (error) {
          console.error("Failed to check video status:", error);
          // Don't stop polling immediately for network errors
        }
      }, 3000); // Poll every 3 seconds
    }
    
    return () => {
      if (pollInterval) {
        console.log("Clearing poll interval");
        clearInterval(pollInterval);
      }
    };
  }, [pollingAnalysisId]);

  const getImpactColor = (impact) => {
    if (impact >= 8) return "bg-red-500";
    if (impact >= 6) return "bg-orange-500";
    if (impact >= 4) return "bg-yellow-500";
    return "bg-gray-500";
  };

  const handleUrlChange = (e) => {
    setYoutubeUrl(e.target.value);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (15MB)
      const maxSize = 15 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`File too large! Maximum size is 15MB. Your file is ${(file.size / (1024*1024)).toFixed(1)}MB`);
        return;
      }
      
      // Validate file type
      const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Unsupported format! Please use MP4, MOV, or AVI files.");
        return;
      }
      
      setSelectedFile(file);
      toast.success(`File selected: ${file.name} (${(file.size / (1024*1024)).toFixed(1)}MB)`);
    }
  };

  const clearAnalysis = () => {
    setAnalysis(null);
    setYoutubeUrl("https://www.youtube.com/watch?v=dQw4w9WgXcQ"); // Reset to default
    setSelectedFile(null);
    setVideoGenerationStatus(null);
    setPollingAnalysisId(null);
    setFartIntensity([5]);
    setFartFrequency([5]);
    setCopyrightAcknowledged(false);
    setActiveTab("youtube"); // Reset to YouTube tab
  };

  const shareVideo = async () => {
    if (analysis?.video_id) {
      const shareUrl = `${BACKEND_URL}/share/${analysis.video_id}`;
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Share link copied to clipboard! 🔗");
      } catch {
        toast.error("Failed to copy link");
      }
    }
  };

  const downloadVideo = () => {
    if (analysis?.generated_video_url) {
      const filename = analysis.generated_video_url.split('/').pop();
      const link = document.createElement('a');
      link.href = `${BACKEND_URL}/api/serve-video/${filename}`;
      link.download = `farted_${analysis.video_id}.mp4`;
      link.click();
      toast.success("Video download started! 🎬");
    }
  };

  // Debug function to test video player
  const testVideoPlayer = () => {
    if (analysis) {
      setAnalysis(prev => ({
        ...prev,
        generated_video_url: "/videos/farted_0315f46c-865b-4a8e-ac0e-3f9df78ca27d.mp4"
      }));
      toast.success("Test video player enabled!");
    }
  };

  // Debug logging when analysis changes
  React.useEffect(() => {
    console.log("Analysis state changed:", analysis);
    if (analysis) {
      console.log("Analysis has generated_video_url:", !!analysis.generated_video_url);
      console.log("Generated video URL value:", analysis.generated_video_url);
    }
  }, [analysis]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}></div>
      
      <div className="relative min-h-screen">
        {/* Header */}
        <div className="container mx-auto px-4 pt-12 pb-8">
          <div className="text-center space-y-4">
            <h1 className="text-6xl font-black text-white mb-4 tracking-tight">
              <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
                FARTVIDS.COM
              </span>
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Every video is better with strategically placed farts. You just didn't know it yet.
              <br />
              <span className="text-yellow-300 font-medium">
                You add the content we add the fluff. And like a really heavy dutch oven these will hang around for about 24hrs.
              </span>
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 pb-12">
          <div className="max-w-4xl mx-auto space-y-8">
            
            {/* Tabbed Input Section */}
            <Card className="bg-white/10 border-white/20 backdrop-blur-lg">
              <CardHeader className="pb-4">
                <CardTitle className="text-white flex items-center space-x-2">
                  <Target className="h-5 w-5" />
                  <span>Add Fart Sounds to Any Video</span>
                </CardTitle>
                <CardDescription className="text-gray-300">
                  Choose a YouTube or Twitter video, or upload your own (max 15MB, 2 minutes)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Tab Navigation */}
                <div className="flex space-x-1 bg-white/5 rounded-lg p-1">
                  <button
                    onClick={() => setActiveTab("youtube")}
                    disabled={isProcessing}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeTab === "youtube"
                        ? "bg-red-600 text-white"
                        : "text-gray-300 hover:text-white hover:bg-white/10"
                    } ${isProcessing ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <Youtube className="h-4 w-4" />
                    <span>Video URL</span>
                  </button>
                  <button
                    onClick={() => setActiveTab("upload")}
                    disabled={isProcessing}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeTab === "upload"
                        ? "bg-blue-600 text-white"
                        : "text-gray-300 hover:text-white hover:bg-white/10"
                    } ${isProcessing ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <FileVideo className="h-4 w-4" />
                    <span>Upload Video</span>
                  </button>
                </div>

                {/* Tab Content */}
                {activeTab === "youtube" && (
                  <div className="space-y-4">
                    {/* YouTube URL Input */}
                    <div className="space-y-3">
                      <label className="text-white text-sm font-medium">Video URL</label>
                      <div className="flex space-x-2">
                        <Input
                          type="text"
                          placeholder="Paste YouTube or Twitter/X video URL here..."
                          value={youtubeUrl}
                          onChange={handleUrlChange}
                          className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                          disabled={isProcessing}
                        />
                      </div>
                      
                      {/* Quick suggestions */}
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                        <p className="text-green-300 text-sm font-medium mb-2">✅ Try these videos (guaranteed to work):</p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => setYoutubeUrl("https://www.youtube.com/watch?v=dQw4w9WgXcQ")}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-full text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isProcessing}
                          >
                            🎵 Rick Roll (YouTube)
                          </button>
                          <button
                            onClick={() => setYoutubeUrl("https://www.youtube.com/watch?v=9bZkp7q19f0")}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-full text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isProcessing}
                          >
                            📺 Gangnam Style (YouTube)
                          </button>
                          <button
                            onClick={() => setYoutubeUrl("https://twitter.com/elonmusk/status/1234567890123456789")}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-full text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isProcessing}
                          >
                            🐦 Twitter Video (X)
                          </button>
                        </div>
                        <p className="text-green-200 text-xs mt-2">
                          💡 Works with YouTube, Twitter/X, and many other video platforms!
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "upload" && (
                  <div className="space-y-4">
                    {/* File Upload */}
                    <div className="space-y-3">
                      <label className="text-white text-sm font-medium">Upload Your Video</label>
                      <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                        isProcessing
                          ? "border-white/10 bg-white/5 opacity-50 cursor-not-allowed"
                          : "border-white/30 hover:border-white/50"
                      }`}>
                        <input
                          type="file"
                          accept="video/mp4,video/quicktime,video/x-msvideo"
                          onChange={handleFileChange}
                          className="hidden"
                          id="video-upload"
                          disabled={isProcessing}
                        />
                        <label 
                          htmlFor="video-upload" 
                          className={`cursor-pointer ${isProcessing ? "cursor-not-allowed" : ""}`}
                        >
                          <FileVideo className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                          {selectedFile ? (
                            <div className="text-white">
                              <p className="font-medium text-lg">{selectedFile.name}</p>
                              <p className="text-sm text-gray-300 mt-1">
                                {(selectedFile.size / (1024*1024)).toFixed(1)}MB
                              </p>
                              <p className="text-xs text-gray-400 mt-2">
                                Click to select a different file
                              </p>
                            </div>
                          ) : (
                            <div>
                              <p className="text-white text-lg mb-1">Click to upload your video</p>
                              <p className="text-gray-300 text-sm">MP4, MOV, AVI • Max 15MB • 2 minutes</p>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Fart Intensity Slider */}
                <div className={`space-y-3 ${isProcessing ? "opacity-50" : ""}`}>
                  <div className="flex items-center justify-between">
                    <label className="text-white font-medium flex items-center space-x-2">
                      <Volume2 className="h-4 w-4" />
                      <span>Fart Intensity</span>
                    </label>
                    <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/30">
                      {getIntensityLabel(fartIntensity[0])}
                    </Badge>
                  </div>
                  <Slider
                    value={fartIntensity}
                    onValueChange={isProcessing ? () => {} : setFartIntensity}
                    max={10}
                    min={1}
                    step={1}
                    className={`w-full ${isProcessing ? "pointer-events-none" : ""}`}
                    disabled={isProcessing}
                  />
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>Subtle Whispers</span>
                    <span>Fully Loaded</span>
                  </div>
                </div>

                {/* Fart Frequency Slider */}
                <div className={`space-y-3 ${isProcessing ? "opacity-50" : ""}`}>
                  <div className="flex items-center justify-between">
                    <label className="text-white font-medium flex items-center space-x-2">
                      <Target className="h-4 w-4" />
                      <span>Fart Frequency</span>
                    </label>
                    <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                      {getFrequencyLabel(fartFrequency[0])}
                    </Badge>
                  </div>
                  <Slider
                    value={fartFrequency}
                    onValueChange={isProcessing ? () => {} : setFartFrequency}
                    max={10}
                    min={1}
                    step={1}
                    className={`w-full ${isProcessing ? "pointer-events-none" : ""}`}
                    disabled={isProcessing}
                  />
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>Subtle</span>
                    <span>Absolute Mayhem</span>
                  </div>
                </div>

                {/* Entertainment Disclaimer */}
                <div className={`flex items-start space-x-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg ${isProcessing ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="entertainment"
                    checked={copyrightAcknowledged}
                    onCheckedChange={isProcessing ? () => {} : setCopyrightAcknowledged}
                    className="border-yellow-500/50 mt-0.5"
                    disabled={isProcessing}
                  />
                  <label htmlFor="entertainment" className={`text-yellow-200 text-sm leading-relaxed ${isProcessing ? "cursor-not-allowed" : "cursor-pointer"}`}>
                    <strong>Entertainment Only:</strong> This is for entertainment purposes only. 
                    Videos automatically delete after 24 hours like a really heavy toot! 💨
                  </label>
                </div>

                {/* Main Action Button */}
                <Button 
                  onClick={analyzeVideo}
                  disabled={isProcessing || (activeTab === "youtube" && !youtubeUrl.trim()) || (activeTab === "upload" && !selectedFile) || !copyrightAcknowledged}
                  className="w-full bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 hover:from-yellow-600 hover:via-orange-600 hover:to-red-600 text-white font-bold text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      {videoGenerationStatus === "processing" ? "Generating video..." : "Adding gas to the vid..."}
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-5 w-5" />
                      💨 Start Adding Gas to the Vid
                    </>
                  )}
                </Button>

                {analysis && (
                  <Button 
                    onClick={clearAnalysis}
                    variant="outline"
                    className="w-full border-white/20 text-gray-300 hover:bg-white/10 mt-4"
                  >
                    ✨ Create Another Masterpiece
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Video Generation Progress */}
            {videoGenerationStatus === "processing" && (
              <Card className="bg-white/10 border-white/20 backdrop-blur-lg">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-400" />
                    <div className="flex-1">
                      <h3 className="text-white font-medium">Generating Farted Video...</h3>
                      <p className="text-gray-300 text-sm">
                        Processing video and adding strategic fart sounds...
                      </p>
                    </div>
                  </div>
                  <Progress value={66} className="mt-4" />
                </CardContent>
              </Card>
            )}

            {/* Video Results */}
            {analysis && (
              <Card className="bg-white/10 border-white/20 backdrop-blur-lg">
                <CardContent className="space-y-6 pt-6">
                  
                  {/* Video Player & Actions */}
                  {analysis.generated_video_url && (
                    <div className="space-y-4">
                      {/* Video Player */}
                      <div className="bg-black/50 rounded-lg p-4 border border-white/20">
                        <h3 className="text-white font-semibold mb-3 flex items-center space-x-2">
                          <Video className="h-5 w-5" />
                          <span>Your Farted Video is Ready!</span>
                        </h3>
                        <div className="aspect-video bg-black rounded-lg overflow-hidden">
                          <video
                            controls
                            className="w-full h-full"
                            poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23000'/%3E%3Ctext x='50' y='50' text-anchor='middle' dy='0.3em' fill='%23fff' font-family='Arial' font-size='12'%3E🎬 Click to Play%3C/text%3E%3C/svg%3E"
                            onError={(e) => {
                              console.error("Video error:", e);
                              console.error("Video source:", e.target.src);
                              console.error("Analysis object at error:", analysis);
                            }}
                            onLoadStart={() => {
                              console.log("Video load started");
                              console.log("Video source:", `${BACKEND_URL}/api/serve-video/${analysis.generated_video_url.split('/').pop()}`);
                              console.log("Analysis object at load:", analysis);
                            }}
                          >
                            <source src={`${BACKEND_URL}/api/serve-video/${analysis.generated_video_url.split('/').pop()}`} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        </div>
                        <p className="text-gray-300 text-sm mt-2 text-center">
                          🎉 <strong>Success!</strong> Your video now has {analysis.placements.length} strategically placed fart sounds. 
                          Watch above, then download or share!
                        </p>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex space-x-3 justify-center">
                        <Button
                          onClick={downloadVideo}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Download Video
                        </Button>
                        <Button
                          onClick={shareVideo}
                          variant="outline"
                          className="border-white/20 text-white hover:bg-white/10"
                        >
                          <Share2 className="mr-2 h-4 w-4" />
                          Share Link
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Video Generation Note */}
                  {!analysis.generated_video_url && (
                    <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4 text-center">
                      <p className="text-yellow-300 text-sm">
                        💡 <strong>Video is being generated...</strong><br />
                        Your farted video will appear here when ready!
                      </p>
                    </div>
                  )}

                  {/* Clear Button */}
                  <Button 
                    onClick={clearAnalysis}
                    variant="outline"
                    className="w-full border-white/20 text-gray-300 hover:bg-white/10"
                  >
                    ✨ Create Another Masterpiece
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;